import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatGuidePrice } from '../../constants/guides';

const GOLD = '#C9A84C';
const CARD_BG = '#141414';
const TEXT = '#F5F0E8';
const MUTED = 'rgba(245, 240, 232, 0.68)';

/**
 * Premium guide product card with CourseModuleCard-style expandable curriculum.
 */
export default function GuideProductCard({ product, isMobile, onBuy }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const borderColor = product.highlight
    ? hovered || open
      ? 'rgba(201, 168, 76, 0.75)'
      : 'rgba(201, 168, 76, 0.55)'
    : hovered || open
      ? 'rgba(201, 168, 76, 0.55)'
      : 'rgba(201, 168, 76, 0.22)';

  return (
    <article
      style={{
        backgroundColor: CARD_BG,
        border: `1px solid ${borderColor}`,
        borderRadius: '14px',
        boxShadow: product.highlight
          ? '0 12px 32px rgba(0,0,0,0.4)'
          : hovered
            ? '0 10px 28px rgba(0,0,0,0.35)'
            : '0 6px 18px rgba(0,0,0,0.22)',
        transform: hovered && !isMobile ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        backgroundImage: product.highlight
          ? 'linear-gradient(180deg, rgba(201,168,76,0.08) 0%, transparent 42%)'
          : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {product.badge ? (
        <div
          style={{
            position: 'absolute',
            top: '0.9rem',
            right: '0.9rem',
            padding: '0.3rem 0.55rem',
            borderRadius: '6px',
            backgroundColor: GOLD,
            color: '#0A0A0A',
            fontSize: '0.625rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {product.badge}
        </div>
      ) : null}

      <div style={{ padding: isMobile ? '1.2rem 1.15rem 1rem' : '1.35rem 1.35rem 1.1rem' }}>
        <h3
          style={{
            margin: 0,
            paddingRight: product.badge ? '5.5rem' : 0,
            fontSize: isMobile ? '1.15rem' : '1.25rem',
            fontWeight: 800,
            color: TEXT,
            lineHeight: 1.25,
          }}
        >
          {product.title}
        </h3>
        <p
          style={{
            margin: '0.55rem 0 0',
            fontSize: '0.9rem',
            color: MUTED,
            lineHeight: 1.55,
            minHeight: isMobile ? undefined : '2.8rem',
          }}
        >
          {product.tagline}
        </p>

        <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'baseline', gap: '0.55rem' }}>
          <span
            style={{
              fontSize: isMobile ? '1.75rem' : '2rem',
              fontWeight: 800,
              color: TEXT,
              letterSpacing: '-0.02em',
            }}
          >
            {formatGuidePrice(product.priceUsd)}
          </span>
          {product.savingsUsd ? (
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: GOLD,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Save {formatGuidePrice(product.savingsUsd)}
            </span>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          borderTop: '1px solid rgba(201, 168, 76, 0.14)',
          cursor: 'pointer',
          padding: isMobile ? '0.9rem 1.15rem' : '0.95rem 1.35rem',
          color: TEXT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
        }}
      >
        <span
          style={{
            fontSize: '0.6875rem',
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(201, 168, 76, 0.9)',
          }}
        >
          {open ? 'Hide what\'s inside' : 'View what\'s inside'}
        </span>
        <ChevronDown
          size={18}
          color={GOLD}
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.22s ease',
          }}
          aria-hidden="true"
        />
      </button>

      <div
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.28s ease',
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }} aria-hidden={!open}>
          <div
            style={{
              borderTop: '1px solid rgba(201, 168, 76, 0.12)',
              margin: '0 1.15rem',
              padding: open ? '0.85rem 0 1.05rem' : '0',
            }}
          >
            <ol
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'grid',
                gap: '0.65rem',
              }}
            >
              {(product.modules || []).map((module) => (
                <li
                  key={`${product.id}-${module.number}`}
                  style={{
                    display: 'flex',
                    gap: '0.65rem',
                    alignItems: 'flex-start',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.6875rem',
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      color: GOLD,
                      flexShrink: 0,
                      marginTop: '0.15rem',
                      minWidth: '1.4rem',
                    }}
                  >
                    {module.number}
                  </span>
                  <span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: TEXT,
                        lineHeight: 1.35,
                      }}
                    >
                      {module.title}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        marginTop: '0.2rem',
                        fontSize: '0.8125rem',
                        color: MUTED,
                        lineHeight: 1.45,
                      }}
                    >
                      {module.description}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div style={{ padding: isMobile ? '0 1.15rem 1.2rem' : '0 1.35rem 1.35rem', marginTop: 'auto' }}>
        <button
          type="button"
          onClick={() => onBuy?.(product)}
          style={{
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.9rem 1.25rem',
            borderRadius: '10px',
            border: `1px solid ${GOLD}`,
            backgroundColor: GOLD,
            color: '#0A0A0A',
            fontWeight: 800,
            fontSize: '0.875rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Buy Now
        </button>
      </div>
    </article>
  );
}
